/**
 * 软键盘弹出时，底部输入区会被键盘盖住。
 * - 嵌入 SDK iframe：面板铺满宿主布局视口不收缩，遮挡高度由宿主 postMessage 下发；
 *   只有宿主也测不出来（Android WebView adjustPan/adjustNothing）才用经验值兜底。
 * - 独立打开：本页 visualViewport / innerHeight 实测。
 */

const VAR_NAME = '--beidou-keyboard-inset';
const HOST_BOTTOM_VAR = '--beidou-host-bottom';
/** 小于该值视为浏览器 UI 抖动，不算键盘 */
const MIN_INSET = 48;
/** 聚焦后先等宿主上报，超时未果再用经验值，避免抬起后又回落的跳变 */
const ESTIMATE_DELAY_MS = 260;

let localInset = 0;
let hostInset = 0;
let hostResolved = false;
let heightBaseline = 0;
let focusedAt = 0;

const isEmbedded = (): boolean =>
  typeof window !== 'undefined' && window.parent !== window;

function flush(): void {
  const inset = Math.max(localInset, hostInset, 0);
  document.documentElement.style.setProperty(VAR_NAME, `${Math.round(inset)}px`);
  document.documentElement.classList.toggle('kb-open', inset >= MIN_INSET);
}

function isInputFocused(): boolean {
  const tag = document.activeElement?.tagName;
  return tag === 'TEXTAREA' || tag === 'INPUT';
}

/** 宿主与本页都测不到键盘时的经验高度（Android 键盘约占视口四成） */
function androidKeyboardEstimate(): number {
  if (!/Android/i.test(navigator.userAgent)) return 0;
  const h = window.innerHeight || 0;
  if (h <= 0) return 0;
  return Math.round(Math.min(400, Math.max(220, h * 0.4)));
}

function measureLocalInset(): number {
  if (isEmbedded()) {
    // 宿主已经给出结论（含 0）就听宿主的，绝不叠加
    if (hostResolved) return 0;
    if (!isInputFocused() || Date.now() - focusedAt < ESTIMATE_DELAY_MS) return 0;
    return androidKeyboardEstimate();
  }

  const vv = window.visualViewport;
  const layoutH = document.documentElement.clientHeight || window.innerHeight || 0;
  let covered = 0;
  if (vv) {
    covered = layoutH - (vv.height + vv.offsetTop);
  }
  const innerDelta = (heightBaseline || layoutH) - (window.innerHeight || 0);
  covered = Math.max(covered, innerDelta);

  if (covered < MIN_INSET && isInputFocused()) {
    covered = Math.max(covered, androidKeyboardEstimate());
  }

  return covered >= MIN_INSET ? covered : 0;
}

/** 宿主页（SDK）实测的键盘遮挡高度，单位 CSS px */
export function setHostKeyboardInset(px: number, resolved = false): void {
  hostInset = Number.isFinite(px) && px >= MIN_INSET ? px : 0;
  hostResolved = Boolean(resolved);
  localInset = measureLocalInset();
  flush();
}

/** 宿主底部 Tab Bar 高度，写入 --beidou-host-bottom 供 safe-area 合并 */
export function setHostBottomInset(px: number): void {
  const v = Number.isFinite(px) && px > 0 ? Math.round(px) : 0;
  document.documentElement.style.setProperty(HOST_BOTTOM_VAR, `${v}px`);
}

/** 挂载监听；返回卸载函数 */
export function installKeyboardInsetTracker(): () => void {
  heightBaseline = window.innerHeight || 0;
  document.documentElement.style.setProperty(HOST_BOTTOM_VAR, '0px');

  const run = () => {
    localInset = measureLocalInset();
    flush();
  };

  const pollAfterFocus = () => {
    let n = 0;
    const tick = () => {
      run();
      if (++n < 12) window.setTimeout(tick, 80);
    };
    tick();
  };

  const onFocusIn = (e: FocusEvent) => {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag !== 'TEXTAREA' && tag !== 'INPUT') return;
    focusedAt = Date.now();
    heightBaseline = Math.max(heightBaseline, window.innerHeight || 0);
    run();
    pollAfterFocus();
  };

  const onFocusOut = (e: FocusEvent) => {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag !== 'TEXTAREA' && tag !== 'INPUT') return;
    focusedAt = 0;
    heightBaseline = window.innerHeight || 0;
    localInset = 0;
    hostInset = 0;
    flush();
  };

  run();
  const vv = window.visualViewport;
  vv?.addEventListener('resize', run);
  vv?.addEventListener('scroll', run);
  window.addEventListener('resize', run);
  window.addEventListener('orientationchange', () => {
    heightBaseline = window.innerHeight || 0;
    run();
  });
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);

  return () => {
    vv?.removeEventListener('resize', run);
    vv?.removeEventListener('scroll', run);
    window.removeEventListener('resize', run);
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
  };
}
